import React, { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { PageHeader, Badge, statusBadge } from "@/components/Common";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PlusIcon, MagnifyingGlassIcon, SnowflakeIcon, PackageIcon, FunnelIcon, UploadIcon } from "@phosphor-icons/react";
import { toast } from "sonner";
import BulkImportDialog from "@/components/BulkImportDialog";
import { sortByUnit } from "@/lib/unitSort";

const TYPES = ["dry_van", "reefer", "flatbed", "tanker", "lowboy", "other"];
const TYPE_LABELS = {
  dry_van: "Dry Van",
  reefer: "Reefer",
  flatbed: "Flatbed",
  tanker: "Tanker",
  lowboy: "Lowboy",
  other: "Other",
};

const empty = {
  unit_number: "",
  vin: "",
  trailer_type: "dry_van",
  make: "",
  model: "",
  year: new Date().getFullYear(),
  length_ft: 53,
  license_plate: "",
  status: "active",
  notes: "",
  reefer_make: "",
  reefer_model: "",
  reefer_serial: "",
  reefer_engine_hours: 0,
};

export function trailerTypeBadge(t) {
  const map = {
    dry_van: { tone: "neutral", label: "Dry Van" },
    reefer: { tone: "info", label: "Reefer" },
    flatbed: { tone: "warning", label: "Flatbed" },
    tanker: { tone: "warning", label: "Tanker" },
    lowboy: { tone: "neutral", label: "Lowboy" },
    other: { tone: "neutral", label: "Other" },
  };
  return map[t] || { tone: "neutral", label: t };
}

export default function Trailers() {
  const nav = useNavigate();
  const [trailers, setTrailers] = useState([]);
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [sortDir, setSortDir] = useState("asc");

  const load = async () => {
    const { data } = await api.get("/trailers");
    setTrailers(data);
  };
  useEffect(() => { load(); }, []);
  useEffect(() => {
    const h = (e) => { if (e?.detail?.entity === "trailers") load(); };
    window.addEventListener("rf-data-change", h);
    return () => window.removeEventListener("rf-data-change", h);
  }, []);

  const save = async (e) => {
    e.preventDefault();
    try {
      const body = {
        ...form,
        year: form.year ? Number(form.year) : null,
        length_ft: form.length_ft ? Number(form.length_ft) : null,
        reefer_engine_hours: form.reefer_engine_hours ? Number(form.reefer_engine_hours) : 0,
      };
      if (editing) await api.put(`/trailers/${editing.id}`, body);
      else await api.post("/trailers", body);
      toast.success(editing ? "Trailer updated" : "Trailer added");
      setOpen(false);
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed");
    }
  };

  const filtered = trailers.filter((t) => {
    if (typeFilter !== "all" && t.trailer_type !== typeFilter) return false;
    const q = search.toLowerCase();
    return (
      t.unit_number?.toLowerCase().includes(q) ||
      t.vin?.toLowerCase().includes(q) ||
      t.make?.toLowerCase().includes(q) ||
      t.model?.toLowerCase().includes(q) ||
      t.license_plate?.toLowerCase().includes(q)
    );
  });
  const sorted = sortByUnit(filtered, sortDir);

  const isReefer = form.trailer_type === "reefer";

  return (
    <div data-testid="trailers-page">
      <PageHeader eyebrow="Trailer fleet" title="Trailers" subtitle="Dry vans, reefers and other trailers. Reefer engine hours are tracked separately.">
        <Button onClick={() => setBulkOpen(true)} variant="outline" className="rounded-sm" data-testid="bulk-import-trailers-button">
          <UploadIcon size={16} weight="bold" className="mr-2" /> Bulk import
        </Button>
        <Button onClick={() => { setEditing(null); setForm(empty); setOpen(true); }} className="bg-slate-900 hover:bg-slate-800 rounded-sm" data-testid="add-trailer-button">
          <PlusIcon size={16} weight="bold" className="mr-2" /> Add Trailer
        </Button>
      </PageHeader>

      <BulkImportDialog
        open={bulkOpen}
        onClose={() => setBulkOpen(false)}
        onSuccess={load}
        kind="trailers"
        title="trailers"
        columnList="UNIT NUMBER · VIN NUMBER · MAKE · MODEL · LICENSE PLATE · TYPE"
      />

      <div className="p-8">
        <div className="bg-white border border-slate-300 rounded-sm">
          <div className="p-3 border-b border-slate-200 flex items-center gap-3 flex-wrap">
            <div className="relative flex-1 max-w-md">
              <MagnifyingGlassIcon size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input
                placeholder="Search unit, VIN, make..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                data-testid="trailer-search"
                className="pl-9 rounded-sm border-slate-300"
              />
            </div>
            <div className="flex items-center gap-2">
              <FunnelIcon size={14} className="text-slate-500" />
              <Select value={typeFilter} onValueChange={setTypeFilter}>
                <SelectTrigger className="w-40 rounded-sm" data-testid="trailer-type-filter"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All types</SelectItem>
                  {TYPES.map((t) => <SelectItem key={t} value={t}>{TYPE_LABELS[t]}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <span className="font-mono text-xs text-slate-500 ml-auto">{filtered.length} of {trailers.length}</span>
          </div>

          {filtered.length === 0 ? (
            <div className="p-12 text-center">
              <PackageIcon size={40} weight="thin" className="mx-auto text-slate-400 mb-2" />
              <div className="font-mono text-xs text-slate-400 mb-2">/// NO TRAILERS</div>
              <div className="text-sm text-slate-600">{trailers.length === 0 ? "Add your first trailer to the fleet." : "No trailers match your filter."}</div>
            </div>
          ) : (
            <table className="fleet-table">
              <thead>
                <tr>
                  <th
                    className="cursor-pointer select-none hover:text-slate-900"
                    onClick={() => setSortDir((d) => (d === "asc" ? "desc" : "asc"))}
                    data-testid="trailers-sort-unit"
                    title="Sort by unit number"
                  >
                    Unit # {sortDir === "asc" ? "▲" : "▼"}
                  </th>
                  <th>Type</th>
                  <th>VIN / Serial</th>
                  <th>Make / Model</th>
                  <th>Year</th>
                  <th>Length</th>
                  <th>Reefer Unit</th>
                  <th>Engine Hours</th>
                  <th>Plate</th>
                  <th>Status</th>
                  <th className="text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {sorted.map((t) => {
                  const sb = statusBadge(t.status);
                  const tb = trailerTypeBadge(t.trailer_type);
                  return (
                    <tr key={t.id} className="clickable" onClick={() => nav(`/trailers/${t.id}`)} data-testid={`trailer-row-${t.unit_number}`}>
                      <td className="font-mono font-bold text-slate-900">{t.unit_number}</td>
                      <td>
                        <Badge tone={tb.tone}>
                          {t.trailer_type === "reefer" && <SnowflakeIcon size={10} weight="bold" />}
                          {tb.label}
                        </Badge>
                      </td>
                      <td className="font-mono text-xs text-slate-600">{t.vin}</td>
                      <td className="font-semibold">{t.make} {t.model}</td>
                      <td className="font-mono">{t.year || "—"}</td>
                      <td className="font-mono">{t.length_ft ? `${t.length_ft} ft` : "—"}</td>
                      <td className="text-xs">{t.trailer_type === "reefer" ? (t.reefer_make ? `${t.reefer_make} ${t.reefer_model}` : "—") : "—"}</td>
                      <td className="font-mono tabular-nums">{t.trailer_type === "reefer" && t.reefer_engine_hours != null ? Number(t.reefer_engine_hours).toLocaleString() : "—"}</td>
                      <td className="font-mono">{t.license_plate || "—"}</td>
                      <td><Badge tone={sb.tone}>{sb.label}</Badge></td>
                      <td className="text-right" onClick={(e) => e.stopPropagation()}>
                        <button
                          onClick={() => {
                            setEditing(t);
                            setForm({ ...empty, ...t });
                            setOpen(true);
                          }}
                          className="text-blue-600 hover:text-blue-800 hover:underline text-xs font-semibold"
                          data-testid={`edit-trailer-${t.unit_number}`}
                        >
                          Edit
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl rounded-sm max-h-[90vh] overflow-y-auto" data-testid="trailer-dialog">
          <DialogHeader>
            <DialogTitle className="font-display tracking-tight">{editing ? "Edit trailer" : "Add new trailer"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={save} className="grid grid-cols-2 gap-4">
            <Field label="Unit Number *" required value={form.unit_number} onChange={(v) => setForm({ ...form, unit_number: v })} tid="trailer-unit" />
            <Field label="VIN / Serial *" required value={form.vin} onChange={(v) => setForm({ ...form, vin: v })} tid="trailer-vin" />
            <div>
              <Label className="text-xs uppercase tracking-wider font-bold text-slate-700">Type *</Label>
              <Select value={form.trailer_type} onValueChange={(v) => setForm({ ...form, trailer_type: v })}>
                <SelectTrigger className="mt-1.5 rounded-sm" data-testid="trailer-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {TYPES.map((t) => <SelectItem key={t} value={t}>{TYPE_LABELS[t]}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider font-bold text-slate-700">Status</Label>
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger className="mt-1.5 rounded-sm" data-testid="trailer-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="maintenance">In Maintenance</SelectItem>
                  <SelectItem value="retired">Retired</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Field label="Make *" required value={form.make} onChange={(v) => setForm({ ...form, make: v })} tid="trailer-make" />
            <Field label="Model" value={form.model} onChange={(v) => setForm({ ...form, model: v })} tid="trailer-model" />
            <Field label="Year" type="number" value={form.year} onChange={(v) => setForm({ ...form, year: v })} tid="trailer-year" />
            <Field label="Length (ft)" type="number" value={form.length_ft} onChange={(v) => setForm({ ...form, length_ft: v })} tid="trailer-length" />
            <Field label="License Plate" value={form.license_plate} onChange={(v) => setForm({ ...form, license_plate: v })} tid="trailer-plate" />

            {isReefer && (
              <div className="col-span-2 border border-blue-200 bg-blue-50 rounded-sm p-4 space-y-3" data-testid="reefer-fields">
                <div className="flex items-center gap-2">
                  <SnowflakeIcon size={16} weight="bold" className="text-blue-700" />
                  <Label className="text-xs uppercase tracking-wider font-bold text-blue-900">Reefer unit details</Label>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs uppercase tracking-wider font-bold text-slate-700">Reefer Make</Label>
                    <Select value={form.reefer_make || "none"} onValueChange={(v) => setForm({ ...form, reefer_make: v === "none" ? "" : v })}>
                      <SelectTrigger className="mt-1.5 rounded-sm bg-white" data-testid="reefer-make"><SelectValue placeholder="Select" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">—</SelectItem>
                        <SelectItem value="Thermo King">Thermo King</SelectItem>
                        <SelectItem value="Carrier">Carrier</SelectItem>
                        <SelectItem value="Other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <Field label="Reefer Model" value={form.reefer_model} onChange={(v) => setForm({ ...form, reefer_model: v })} tid="reefer-model" />
                  <Field label="Reefer Serial #" value={form.reefer_serial} onChange={(v) => setForm({ ...form, reefer_serial: v })} tid="reefer-serial" />
                  <Field label="Engine Hours" type="number" step="0.1" value={form.reefer_engine_hours} onChange={(v) => setForm({ ...form, reefer_engine_hours: v })} tid="reefer-hours" />
                </div>
                <p className="text-[11px] text-slate-600 font-mono">
                  Reefer oil changes are tracked by engine hours, not mileage.
                </p>
              </div>
            )}

            <div className="col-span-2">
              <Label className="text-xs uppercase tracking-wider font-bold text-slate-700">Notes</Label>
              <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="mt-1.5 rounded-sm" data-testid="trailer-notes" />
            </div>
            <DialogFooter className="col-span-2 mt-2">
              <Button type="button" variant="outline" onClick={() => setOpen(false)} className="rounded-sm">Cancel</Button>
              <Button type="submit" className="bg-slate-900 hover:bg-slate-800 rounded-sm" data-testid="trailer-save">{editing ? "Update" : "Create"}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", required = false, tid, step }) {
  return (
    <div>
      <Label className="text-xs uppercase tracking-wider font-bold text-slate-700">{label}</Label>
      <Input type={type} step={step} value={value} onChange={(e) => onChange(e.target.value)} required={required} className="mt-1.5 rounded-sm" data-testid={tid} />
    </div>
  );
}
